/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { CarePlanService } from 'app/cm/services/care-plan-service';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { I18N } from 'aurelia-i18n';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import { Redirect } from 'aurelia-router';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';
import * as Mcg from 'app/ext/mcg-informed/interfaces';

@autoinject()
export class EditBarrierPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected barrierId!: string;
    protected barrier: CM.CarePlanBarrier = { id: null };
    protected mcgCopyright?: string;
    protected mcgCustomContentDisclaimer?: string;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'cm/careplans/barriers',
        targetType: 'CM.CarePlanBarrier',
        addTargetProperty: this.addBarrierProperty.bind(this),
        excludeSystem: false
    };


    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: CasePageContext,
        private readonly service: CarePlanService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate({ caseId, goalId, barrierId }: { caseId: string, goalId: string, barrierId: string }): Promise<Redirect | true> {
        if(!(caseId && goalId && barrierId)) {
            throw new Error('Required parameters [caseId] and/or [goalId] and/or [barrierId] were not found');
        }

        try {
            this.barrierId = barrierId;

            if(!this.context.case.number || this.context.case.id !== caseId) {
                await this.context.loadCase(caseId, undefined, ['number']);
            }

            await this.loadBarrier();

            const barrierCaseId = this.barrier.case?.id;
            if(barrierCaseId !== caseId) {
                // reroute user if barrier does not belong to the case
                const messageOptions = {
                    what: this.i18n.tr('cm:carePlanBarrier.$'),
                    item: this.i18n.tr('cm:case.$').toLowerCase(),
                    number: this.context.case.number
                };
                const message = this.i18n.tr('ui:messages.errors.doesNotExistFor', messageOptions);
                this.toaster.error().message(message).show();
                return new Redirect('careplan');
            }
            return true;
        } catch(error) {
            throw error;
        }
    }

    protected async activate(): Promise<void> {
        if(this.barrier.externalSystem && this.barrier.externalSystem.code?.toLowerCase() == 'cci') {
            // Set copyright text based on current barrier's external assessment version
            const copyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();
            const edition = this.barrier.goal?.externalAssessmentVersion ? this.barrier.goal.externalAssessmentVersion.split('.')[0] : getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
            if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);
        }
    }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    private addBarrierProperty(note: CM.CarePlanBarrierNote): void {
        note.barrier = { id: this.barrier.id };
    }

    public navigateBack(): void {
        this.router.navigateBack();
    }

    protected getCardTitle(): string {
        return (this.barrier.externalSystem && this.barrier.externalSystem.code === 'CCI' ? `${this.i18n.tr('ext:cci.barrier.$long')}` : `${this.i18n.tr('cm:carePlanBarrier.$')}`) + `: ${this.barrier.description}`;
    }

    ////****************** Service Calls ********************************************////
    private async loadBarrier(): Promise<void> {
        if(!this.barrierId) return;

        const includes = [
            'case',
            'externalSystem',
            'goal',
            'goal.assessmentAttachment',
            'goal.assessmentAttachment.externalContent',
            'member',
            'metadata.created.byUser'
        ];

        const fields = [
            'case.id',
            'description',
            'externalSystem.code',
            'goal.assessmentAttachment.externalContent.content',
            'goal.externalAssessment.version',
            'member.name.full',
            'metadata.created.byUser.name.full',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadBarrier(this.barrierId, includes, fields);
            this.barrier = response.results[0];

            // Set custom content disclaimer based on parsed content
            if(this.barrier.goal?.assessmentAttachment?.externalContent?.content) {
                const content = this.barrier.goal.assessmentAttachment.externalContent.content;
                const assessment: Mcg.Assessment = JSON.parse(content);
                this.mcgCustomContentDisclaimer = assessment.customGuidelineDisclaimer;
            }
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
